import { useState } from 'react'
import { useRoundTable, useSpeakInRoundTable, useResolveRoundTable } from '../../hooks/useRoundTables'
import type { RoundTable } from '../../types/round-tables'

interface RoundTableDetailProps {
  rtId: string
  onBack: () => void
}

function formatTime(ts: string): string {
  return new Date(ts).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
}

function formatDate(ts: string): string {
  return new Date(ts).toLocaleString()
}

export function RoundTableDetail({ rtId, onBack }: RoundTableDetailProps) {
  const { data: rt, isLoading, error } = useRoundTable(rtId)
  const speakMutation = useSpeakInRoundTable()
  const resolveMutation = useResolveRoundTable()

  const [speakText, setSpeakText] = useState('')
  const [resolveText, setResolveText] = useState('')
  const [showResolveForm, setShowResolveForm] = useState(false)
  const [speakError, setSpeakError] = useState<string | null>(null)
  const [resolveError, setResolveError] = useState<string | null>(null)

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-swiss-body text-swiss-gray-500">Loading...</div>
      </div>
    )
  }

  if (error || !rt) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[400px]">
        <div className="text-swiss-body text-swiss-red mb-4">
          {error instanceof Error ? error.message : 'Failed to load round table'}
        </div>
        <button
          onClick={onBack}
          className="text-swiss-small text-swiss-black underline underline-offset-2 cursor-pointer"
        >
          Back
        </button>
      </div>
    )
  }

  const isOpen = rt.status === 'open'

  const handleSpeak = async () => {
    if (!speakText.trim()) return
    setSpeakError(null)
    try {
      await speakMutation.mutateAsync({ id: rtId, data: { message: speakText.trim() } })
      setSpeakText('')
    } catch (err) {
      setSpeakError(err instanceof Error ? err.message : 'Failed to send message')
    }
  }

  const handleResolve = async () => {
    if (!resolveText.trim()) return
    setResolveError(null)
    try {
      await resolveMutation.mutateAsync({ id: rtId, data: { decision: resolveText.trim() } })
      setShowResolveForm(false)
      setResolveText('')
    } catch (err) {
      setResolveError(err instanceof Error ? err.message : 'Failed to resolve round table')
    }
  }

  return (
    <div>
      {/* Header */}
      <div className="mb-6 flex items-start justify-between">
        <div className="flex items-start gap-4">
          <button
            onClick={onBack}
            className="mt-0.5 text-swiss-gray-400 hover:text-swiss-black text-[13px] cursor-pointer transition-colors duration-150 underline underline-offset-2"
          >
            ← Back
          </button>
          <div>
            <h1 className="text-[20px] font-semibold tracking-tight leading-[1.2]">{rt.topic}</h1>
            {rt.goal && (
              <p className="text-swiss-gray-500 mt-1 text-[13px]">{rt.goal}</p>
            )}
            <div className="flex items-center gap-3 mt-2">
              <StatusBadge status={rt.status} />
              <span className="text-swiss-caption font-swiss-mono text-swiss-gray-400">
                {rt.facilitator}
              </span>
              <span className="text-swiss-caption text-swiss-gray-300">·</span>
              <span className="text-swiss-caption font-swiss-mono text-swiss-gray-400">
                {rt.participants.join(', ')}
              </span>
            </div>
          </div>
        </div>
        <div className="text-swiss-caption font-swiss-mono text-swiss-gray-400 text-right">
          <div>Created {formatDate(rt.created_at)}</div>
          {isOpen && <div className="text-swiss-red">Expires {formatDate(rt.expires_at)}</div>}
        </div>
      </div>

      {/* Decision block (pinned above thread when resolved) */}
      {rt.status === 'resolved' && rt.decision && (
        <div className="border-l-4 border-swiss-green bg-swiss-off-white px-5 py-4 mb-6">
          <div className="text-swiss-caption font-medium text-swiss-green mb-1">DECISION</div>
          <div className="text-[14px] text-swiss-black">{rt.decision.outcome}</div>
          {rt.decision.structured && (
            <div className="mt-3 space-y-1">
              {Object.entries(rt.decision.structured).map(([k, v]) => (
                <div key={k} className="flex gap-3 text-swiss-caption font-swiss-mono">
                  <span className="text-swiss-gray-400 min-w-[120px]">{k}</span>
                  <span className="text-swiss-black">{String(v)}</span>
                </div>
              ))}
            </div>
          )}
          <div className="text-swiss-caption font-swiss-mono text-swiss-gray-400 mt-2">
            Resolved by {rt.decision.resolved_by} · {formatDate(rt.decision.resolved_at)}
          </div>
        </div>
      )}

      {/* Thread */}
      <div className="mb-6">
        <div className="text-swiss-caption font-medium text-swiss-gray-500 mb-3">THREAD</div>
        {!rt.thread || rt.thread.length === 0 ? (
          <div className="bg-swiss-off-white border border-swiss-gray-200 p-8 text-center">
            <div className="text-swiss-small text-swiss-gray-400">No messages yet</div>
          </div>
        ) : (
          <div className="border border-swiss-gray-200">
            {/* Table header */}
            <div className="grid grid-cols-[80px_160px_1fr] border-b border-swiss-gray-200 px-4 py-2 bg-swiss-off-white">
              <div className="text-swiss-caption font-medium text-swiss-gray-500">TIME</div>
              <div className="text-swiss-caption font-medium text-swiss-gray-500">FROM</div>
              <div className="text-swiss-caption font-medium text-swiss-gray-500">MESSAGE</div>
            </div>
            {rt.thread.map((entry) => (
              <div
                key={entry.id}
                className="grid grid-cols-[80px_160px_1fr] border-b border-swiss-gray-100 px-4 py-3 hover:bg-swiss-off-white/50"
              >
                <div className="text-swiss-caption font-swiss-mono text-swiss-gray-400 pt-0.5">
                  {formatTime(entry.timestamp)}
                </div>
                <div className="text-swiss-caption font-swiss-mono text-swiss-black pr-3">
                  {entry.from}
                </div>
                <div className="text-[13px] text-swiss-black whitespace-pre-wrap">{entry.message}</div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Action bar (only for open sessions) */}
      {isOpen && (
        <div className="border-t border-swiss-gray-200 pt-6 space-y-4">
          {/* Speak */}
          <div>
            <label className="block text-swiss-caption font-medium text-swiss-gray-600 mb-2">
              SPEAK INTO SESSION
            </label>
            <textarea
              value={speakText}
              onChange={(e) => setSpeakText(e.target.value)}
              placeholder="Add a message to this round table..."
              rows={3}
              className="w-full px-3 py-2 text-[13px] border border-swiss-gray-200 bg-swiss-white text-swiss-black placeholder:text-swiss-gray-300 focus:outline-none focus:border-swiss-black resize-none"
            />
            {speakError && (
              <div className="text-swiss-caption text-swiss-red mt-1">{speakError}</div>
            )}
            <div className="flex items-center gap-3 mt-2">
              <button
                onClick={handleSpeak}
                disabled={speakMutation.isPending || !speakText.trim()}
                className="px-4 py-2 text-[13px] bg-swiss-black text-swiss-white cursor-pointer hover:bg-swiss-gray-700 transition-colors duration-150 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {speakMutation.isPending ? 'Sending...' : 'Speak'}
              </button>

              {/* Pause — disabled, coming soon */}
              <button
                disabled
                title="Pause coming soon"
                className="px-4 py-2 text-[13px] border border-swiss-gray-200 text-swiss-gray-300 cursor-not-allowed"
              >
                Pause
              </button>

              {/* Resolve */}
              <button
                onClick={() => setShowResolveForm(!showResolveForm)}
                className="px-4 py-2 text-[13px] border border-swiss-gray-200 text-swiss-black cursor-pointer hover:border-swiss-black transition-colors duration-150 ml-auto"
              >
                {showResolveForm ? 'Cancel' : 'Resolve'}
              </button>
            </div>
          </div>

          {/* Resolve form */}
          {showResolveForm && (
            <div className="border border-swiss-gray-200 p-4">
              <label className="block text-swiss-caption font-medium text-swiss-gray-600 mb-2">
                DECISION / OUTCOME
              </label>
              <textarea
                value={resolveText}
                onChange={(e) => setResolveText(e.target.value)}
                placeholder="Summarize the decision made in this session..."
                rows={3}
                className="w-full px-3 py-2 text-[13px] border border-swiss-gray-200 bg-swiss-white text-swiss-black placeholder:text-swiss-gray-300 focus:outline-none focus:border-swiss-black resize-none"
              />
              {resolveError && (
                <div className="text-swiss-caption text-swiss-red mt-1">{resolveError}</div>
              )}
              <button
                onClick={handleResolve}
                disabled={resolveMutation.isPending || !resolveText.trim()}
                className="mt-2 px-4 py-2 text-[13px] bg-swiss-green text-swiss-white cursor-pointer hover:opacity-90 transition-opacity duration-150 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {resolveMutation.isPending ? 'Resolving...' : 'Mark Resolved'}
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

function StatusBadge({ status }: { status: RoundTable['status'] }) {
  if (status === 'open') {
    return (
      <span className="inline-block px-2 py-0.5 text-swiss-caption font-medium bg-swiss-black text-swiss-white">
        ACTIVE
      </span>
    )
  }
  if (status === 'resolved') {
    return (
      <span className="inline-block px-2 py-0.5 text-swiss-caption font-medium bg-swiss-off-white text-swiss-green border border-swiss-gray-200">
        RESOLVED
      </span>
    )
  }
  return (
    <span className="inline-block px-2 py-0.5 text-swiss-caption font-medium bg-swiss-off-white text-swiss-red border border-swiss-gray-200">
      EXPIRED
    </span>
  )
}
